import { Suspense } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Layers } from 'lucide-react'
import { AdminVersionsScreen } from '@/components/admin/admin-versions-screen'
import { ViewBar } from '@/components/shell/view-bar'
import { RowsSkeleton, ViewBarSkeleton } from '@/components/shell/screen-skeletons'
import { Skeleton } from '@/components/ui/skeleton'
import { adminAppVersionsQuery } from '@/lib/queries/admin'

/**
 * Platform administration — which build everyone is running.
 *
 * Sits under /o/$org alongside the organizations directory for the same reason:
 * administrators keep the shell and their bearings, and the org in the URL
 * scopes nothing here. No route guard — the IAM policies give a
 * non-administrator their own row and nothing else, and the sidebar only shows
 * the link to admins.
 */
export const Route = createFileRoute('/o/$org/admin/versions')({
  loader: ({ context: { queryClient } }) => {
    /* Not awaited — same pattern as the organizations directory and the org
       route. Only the first page; the screen pulls the rest itself, because its
       counts are not correct until it has them all. */
    void queryClient.prefetchInfiniteQuery(adminAppVersionsQuery())
  },

  pendingComponent: () => (
    <>
      <ViewBarSkeleton />
      <RowsSkeleton />
    </>
  ),

  component: AdminVersionsRoute,
})

function AdminVersionsRoute() {
  return (
    <>
      <ViewBar
        icon={<Layers />}
        crumbs={[{ label: 'Platform' }]}
        title="App Versions"
      />
      <Suspense fallback={<VersionsSkeleton />}>
        <AdminVersionsScreen />
      </Suspense>
    </>
  )
}

function VersionsSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
      <Skeleton className="h-5 w-96 max-w-full" />
      <div className="border-border divide-border shrink-0 divide-y overflow-hidden rounded-xl border">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Skeleton className="size-4 shrink-0 rounded" />
            <Skeleton className="h-3.5 w-40 max-w-full flex-1" />
            <Skeleton className="h-3.5 w-10 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  )
}
